import { useEffect, useRef, useState } from 'react';
import { Box3, Vector3, type Object3D, type PerspectiveCamera } from 'three';
import type { PublicCharacterInfo } from '../api/fan-api';
import { applyPersistedModel3DConfig } from '../embed/runtime/model3d-config';
import { loadSiblingMorphPack } from '../embed/runtime/model3d-morph-pack';
import type { DraftCharacter } from '@nxorbit/shared-schema';

interface Props {
  info: PublicCharacterInfo;
  className?: string;
  framing?: 'default' | 'card-cover' | 'hero-cover';
  /**
   * When false, the runtime mounts but OrbitControls (rotate / zoom /
   * pan) are disabled. Used in the preview-only state to give un-
   * enrolled fans a taste of the model without full interaction.
   * Defaults to true.
   */
  interactive?: boolean;
}

type CoverFraming = Exclude<Props['framing'], undefined | 'default'>;

function applyCoverFraming(controller: unknown, framing: CoverFraming): void {
  const api = controller as {
    getThreeContext?: () => {
      camera: PerspectiveCamera;
      controls: { target: Vector3; update: () => void } | null;
    } | null;
    getModelRoot?: () => Object3D | null;
  };
  const ctx = api.getThreeContext?.();
  const root = api.getModelRoot?.();
  if (!ctx || !root) return;

  const box = new Box3().setFromObject(root);
  const size = new Vector3();
  const center = new Vector3();
  box.getSize(size);
  box.getCenter(center);
  if (!Number.isFinite(size.y) || size.y <= 0) return;

  const camera = ctx.camera;
  const fovRad = (camera.fov * Math.PI) / 180;
  const fill = framing === 'hero-cover' ? 1.32 : 1.2;
  const distance = (size.y / fill / 2) / Math.tan(fovRad / 2);
  const targetY = box.min.y + size.y * (framing === 'hero-cover' ? 0.6 : 0.56);

  camera.position.set(center.x, targetY + 0.08, center.z + distance);
  camera.lookAt(center.x, targetY, center.z);
  camera.near = Math.max(0.001, distance * 0.01);
  camera.far = Math.max(camera.far, distance * 10);
  camera.updateProjectionMatrix();
  if (ctx.controls) {
    ctx.controls.target.set(center.x, targetY, center.z);
    ctx.controls.update();
  }
}

/**
 * Minimal 3D mount for the fan platform. Loads model3d-runtime
 * lazily (large vendor chunk), creates the shader-composed controller,
 * mounts it to the host div, and disposes on unmount.
 *
 * No chat / action plan integration in v1 — just the character in
 * idle. Default clip from animationMap plays automatically per the
 * runtime's own logic. Expression / action handling can be wired up
 * later by passing the chat reply into the controller's `applyAction`
 * (currently the chat reply is text-only so the avatar stays calm).
 */
export function Character3DStage({ info, className, framing = 'default', interactive = true }: Props) {
  const hostRef = useRef<HTMLDivElement>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    setLoaded(false);
    const host = hostRef.current;
    if (!host || !info.model3d?.modelPath) return;
    let cancelled = false;
    let disposer: (() => void) | null = null;

    void import('@nxorbit/model3d-runtime').then(async (mod) => {
      if (cancelled) return;
      try {
        // Keep fan-facing previews aligned with the operator preview:
        // (e.g. /samples/Malfeuera/Texture/Base.png). These resolve
        // against the current SPA origin (fan.cloudfront.net) which
        // doesn't host /samples/ — they live on admin-ui. Loading
        // them 404s and the runtime errors out the whole mount.
        // Falling back to the GLB's baked-in materials renders fine
        // (just no per-mesh eye/mouth swap regions).
        //
        // The pack schema (see shared-schema/model3d-pack.ts) is a
        // flat object of URL strings — `base`, `eyeMask`, `eyeNormal`,
        // `eyeBlink`, `mouthMask`, plus `visemes.{idle,A,E,I,O,U}` and
        // `expressions[].url`. The previous detector looked for a
        // non-existent `textures` map, so it always returned false and
        // the bad pack passed through. This walks the real fields.
        const loadedFallback = info.model3d!.morphPack
          ? null
          : await loadSiblingMorphPack(info.model3d!.modelPath).catch(() => null);
        if (cancelled) return;
        const pack = info.model3d!.texturePack as never | undefined;
        const morphPack = (info.model3d!.morphPack ?? loadedFallback?.pack) as never | undefined;
        const controller = mod.createShaderComposed3DController({
          animationMap: info.model3d!.animationMap,
          transparent: true,
        });
        disposer = () => controller.dispose();
        await controller.mount(host, {
          url: info.model3d!.modelPath,
          format: info.model3d!.modelFormat,
          texturePack: pack,
          morphPack,
          expressionMode: morphPack ? 'morphTargets' : info.model3d!.expressionMode,
          proceduralActions: (info.model3d as unknown as { proceduralActions?: import('@nxorbit/shared-schema').ProceduralAction[] })?.proceduralActions ?? [],
        } as never);
        if (cancelled) {
          controller.dispose();
          return;
        }

        // Load operator base + fan-overlay scene objects via the same
        // sequential attach loop EmbedRuntime uses. Without this, the
        // market preview shows the bare character mesh while the chat
        // view renders the full composed scene — fans were confused
        // by the discrepancy.
        const sceneObjects = info.model3d!.sceneObjects ?? [];
        if (sceneObjects.length > 0) {
          const cWithAttach = controller as unknown as {
            attachSceneObject?: (spec: typeof sceneObjects[number]) => Promise<void>;
          };
          for (const obj of sceneObjects) {
            if (cancelled) break;
            try { await cWithAttach.attachSceneObject?.(obj); } catch (e) {
              console.warn('[Character3DStage] attachSceneObject failed', { id: obj.id, err: (e as Error).message });
            }
          }
        }

        // Apply persisted runtime config — same helper used by
        // admin Preview + EmbedRuntime so the fan market view matches.
        // Runtime expects DraftCharacter shape; PublicCharacterInfo
        // exposes the same model3d sub-fields (clipLabels,
        // defaultClipLabels, renderParams, viewportPose,
        // autoReturnToNature) so we wrap and cast.
        applyPersistedModel3DConfig(
          controller as unknown as Parameters<typeof applyPersistedModel3DConfig>[0],
          { model3d: info.model3d } as unknown as DraftCharacter,
        );
        if (framing !== 'default') applyCoverFraming(controller, framing);
        const cAny = controller as unknown as {
          setInteractive?: (v: boolean) => void;
          setOrbitControlsEnabled?: (v: boolean) => void;
          controls?: { enableRotate: boolean; enableZoom: boolean; enablePan: boolean };
        };
        if (!interactive) {
          cAny.setInteractive?.(false);
          cAny.setOrbitControlsEnabled?.(false);
          if (cAny.controls) {
            cAny.controls.enableRotate = false;
            cAny.controls.enableZoom = false;
            cAny.controls.enablePan = false;
          }
        } else {
          cAny.setOrbitControlsEnabled?.(true);
          if (cAny.controls) {
            cAny.controls.enableRotate = true;
            cAny.controls.enableZoom = false;
            cAny.controls.enablePan = false;
          }
        }
        if (!cancelled) setLoaded(true);
      } catch (err) {
        console.error('[Character3DStage] mount failed', err);
      }
    }).catch((err) => {
      console.error('[Character3DStage] import failed', err);
    });

    return () => {
      cancelled = true;
      disposer?.();
    };
  }, [info, framing, interactive]);

  // Wrap the host in a relative container so we can overlay a loader
  // while the GLB / texture pack downloads. The overlay fades out on
  // first successful mount; consumer styling on the host is preserved
  // via the passed-through className.
  const hostClass = className ?? 'w-full aspect-square bg-slate-950 rounded-lg overflow-hidden';
  return (
    <div className="relative h-full w-full overflow-hidden">
      <div
        ref={hostRef}
        className={hostClass}
        aria-label={`${info.name} 的 3D 模型`}
      />
      <div
        aria-hidden="true"
        className={[
          'pointer-events-none absolute inset-0 grid place-items-center rounded-lg transition-opacity duration-500',
          loaded ? 'opacity-0' : 'opacity-100',
        ].join(' ')}
        style={{
          background:
            'radial-gradient(circle at 50% 50%, rgba(124,70,243,0.20) 0%, transparent 65%)',
        }}
      >
        {/* Orbiting ring loader — pure CSS, GPU compositor only. */}
        <div className="relative h-12 w-12">
          <span className="absolute inset-0 rounded-full border-2 border-slate-700/40" />
          <span
            className="absolute inset-0 rounded-full border-2 border-transparent"
            style={{
              borderTopColor: 'rgb(167, 139, 250)',
              borderRightColor: 'rgb(56, 189, 248)',
              animation: 'nx-3d-spin 1.1s linear infinite',
            }}
          />
          <span
            className="absolute inset-2 rounded-full"
            style={{
              background: 'radial-gradient(circle, rgba(124,70,243,0.6), transparent 70%)',
              animation: 'nx-3d-pulse 1.8s ease-in-out infinite',
            }}
          />
        </div>
      </div>
    </div>
  );
}
